import React, { useEffect, useState } from 'react';

import {
  FormControl,
  FormHelperText,
  InputBaseComponentProps,
  InputLabel,
  OutlinedInput,
  SxProps,
  useTheme,
} from '@mui/material';
import { INTEGER_REGEXP, NUMBER_REGEXP } from '../../constants/regEx';

export interface TextFieldProps {
  placeholder?: string;
  label?: string;
  endAdornment?: React.ReactNode;
  startAdornment?: React.ReactNode;
  className?: string;
  inputClassName?: string;
  type?: 'number' | 'integer' | 'string' | 'password' | 'text';
  style?: React.CSSProperties;
  disabled?: boolean;
  value?: string | number;
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  onBlur?: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  onPaste?: React.ClipboardEventHandler<HTMLDivElement>;
  onClick?: React.MouseEventHandler<HTMLDivElement> | undefined;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  error?: string;
  name?: string;
  multiline?: boolean;
  required?: boolean;
  number?: boolean;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  inputRef?: React.Ref<any>;
  sx?: SxProps;
  rows?: number;
  showInputProps?: boolean;
  maxLength?: number;
  helperText?: string;
  showCharCount?: boolean;
}

function SbTextField(props: TextFieldProps) {
  const { type, showInputProps = true, maxLength, value = '', showCharCount } = props;
  const [charCount, setCharCount] = useState<number | undefined>(value ? String(value).length : undefined);
  const theme = useTheme();

  useEffect(() => {
    if (value) {
      setCharCount(String(value).length);
    } else {
      setCharCount(undefined);
    }
  }, [value]);

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (props.onChange) {
      if (type === 'number' || type === 'integer') {
        const regExp = type === 'number' ? NUMBER_REGEXP : INTEGER_REGEXP;
        if (regExp.test(event.target.value) || event.target.value === '') {
          props.onChange(event);
          setCharCount(value.length);
        }
      } else {
        props.onChange(event);
        setCharCount(value.length);
      }
    }
  };

  const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
    if (type === 'number' || type === 'integer') {
      (e.target as HTMLInputElement).blur();
    }
  };

  const inputProps: InputBaseComponentProps = {
    ...(showInputProps && type === 'number' ? { type: 'number', step: '0.01', min: '1' } : {}),
    ...(maxLength ? { maxLength: maxLength } : {}),
  };

  const hasError = !!props.error;

  return (
    <div style={props.style || {}} className={`sb-textfield-wrapper ${props.className || ''}`}>
      {props.label && (
        <InputLabel
          className="sb-textfield-label"
          required={props.required}
          sx={{
            fontWeight: 500,
            fontSize: '0.875rem',
            color: hasError ? theme.palette.error.main : theme.palette.text.primary,
            marginBottom: '8px',
            display: 'block',
            letterSpacing: '0.3px',
            transition: 'color 0.2s ease',
            '&.Mui-focused': {
              color: hasError ? theme.palette.error.main : theme.palette.primary.main,
            },
          }}
        >
          {props.label}
        </InputLabel>
      )}
      <FormControl className={`sb-textfield-control ${props.inputClassName || ''}`} fullWidth error={hasError}>
        <OutlinedInput
          multiline={props.multiline}
          rows={props.rows}
          error={hasError}
          name={props.name}
          type={props.type === 'number' ? 'string' : props.type}
          className="sb-textfield-input"
          placeholder={props.placeholder || ''}
          endAdornment={props.endAdornment || <></>}
          startAdornment={props.startAdornment || <></>}
          sx={{
            ...props.sx,
            // Glass-morphism background
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(18, 18, 18, 0.7)' : 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',

            // Remove default outlined border
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },

            // Custom border
            border: hasError ? `1px solid ${theme.palette.error.main}` : `1px solid ${theme.palette.divider}`,

            // Input styling
            '& .MuiOutlinedInput-input': {
              fontSize: '0.95rem',
              fontWeight: 400,
              color: theme.palette.text.primary,
              padding: '12px 16px',
              letterSpacing: '0.3px',
              transition: 'all 0.2s ease',

              '&::placeholder': {
                color: theme.palette.text.secondary,
                opacity: 0.6,
              },

              '&:autofill, &:-webkit-autofill': {
                WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.paper} inset !important`,
                WebkitTextFillColor: `${theme.palette.text.primary} !important`,
              },
            },

            // Focus state - subtle border and glow
            '&.Mui-focused': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(18, 18, 18, 0.85)' : 'rgba(255, 255, 255, 0.85)',
              backdropFilter: 'blur(12px)',
              border: hasError ? `1px solid ${theme.palette.error.main}` : `1px solid ${theme.palette.primary.main}`,
              boxShadow: hasError
                ? `0 0 0 2px ${theme.palette.error.main}20`
                : `0 0 0 2px ${theme.palette.primary.main}20`,
            },

            // Hover state
            '&:hover:not(.Mui-focused):not(.Mui-disabled)': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(18, 18, 18, 0.8)' : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(12px)',
              borderColor: hasError ? theme.palette.error.main : theme.palette.primary.main,
            },

            // Disabled state
            '&.Mui-disabled': {
              backgroundColor: theme.palette.action.disabledBackground,
              backdropFilter: 'blur(8px)',
              borderColor: theme.palette.divider,
              cursor: 'not-allowed',

              '& .MuiOutlinedInput-input': {
                color: theme.palette.text.disabled,
                WebkitTextFillColor: theme.palette.text.disabled,
                cursor: 'not-allowed',
              },
            },

            // Multiline adjustments
            '&.MuiOutlinedInput-multiline': {
              padding: 0,

              '& .MuiOutlinedInput-input': {
                padding: '12px 16px',
              },
            },

            // Adornments
            '& .MuiInputAdornment-root': {
              color: theme.palette.text.secondary,
              transition: 'color 0.2s ease',
            },

            '&.Mui-focused .MuiInputAdornment-root': {
              color: theme.palette.primary.main,
            },
          }}
          disabled={!!props.disabled}
          value={props.value || ''}
          onChange={onChange}
          inputRef={props.inputRef}
          onBlur={props.onBlur}
          required={props.required}
          onPaste={props.onPaste}
          inputProps={inputProps}
          onClick={props.onClick}
          onKeyDown={props.onKeyDown}
          onWheel={handleWheel}
        />

        {/* Error Message */}
        {props.error && (
          <FormHelperText
            error
            sx={{
              margin: '6px 4px 0 4px',
              fontSize: '0.75rem',
              fontWeight: 500,
              color: theme.palette.error.main,
              letterSpacing: '0.2px',
              animation: 'slideInDown 0.2s ease',

              '@keyframes slideInDown': {
                from: {
                  opacity: 0,
                  transform: 'translateY(-4px)',
                },
                to: {
                  opacity: 1,
                  transform: 'translateY(0)',
                },
              },
            }}
          >
            {typeof props.error === 'string' ? props.error : 'Invalid Data'}
          </FormHelperText>
        )}

        {/* Character Count */}
        {showCharCount && charCount !== undefined && (
          <div
            style={{
              textAlign: 'right',
              marginTop: '6px',
              fontSize: '0.7rem',
              fontWeight: 500,
              color: hasError ? theme.palette.error.main : theme.palette.text.secondary,
              letterSpacing: '0.2px',
              transition: 'color 0.2s ease',
            }}
          >
            {charCount}
            {maxLength ? `/${maxLength}` : ''}
          </div>
        )}
      </FormControl>
    </div>
  );
}

export default SbTextField;
